import React, { useState } from "react";
import "./education.css";
import { useNavigate } from "react-router-dom";
import {
  resetOnboardingInfo,
  updateOnboardingInfo,
} from "../../redux/attorneyOnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import BackIcon from "./assets/backIcon.png";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import AddIcon from "./assets/addIcon.png";
import NarrowDropdownHierarchy from "../../components/input/new/narrowDropdownHierarchy";
import StepFooter from "./stepFooter";
import RadioButton from "../../components/input/new/radioButton";
import {
  schools,
  degrees,
  positions,
  professionalMemberships,
} from "./schoolData";

const Education = () => {
  const userId = useSelector(
    (state) => state.attorneyData.attorneyData?.user_id
  );

  const [education, setEducation] = useState([]);
  const [selectedMemberships, setSelectedMemberships] = useState([]);
  const [noInstitution, setNoInstitution] = useState(false);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 70 }, (_, index) => {
    const year = currentYear - index;
    return { label: String(year), value: String(year) };
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goBackToProfessionals = () => {
    dispatch(resetOnboardingInfo());
    navigate(`/attorney/${userId}/home`);
  };

  const addEducation = () => {
    setEducation((prev) => [
      ...prev,
      {
        institution: "",
        degree: "",
        startYear: "",
        endYear: "",
        // Set the first added row as primary; others false by default.
        primary: prev.length === 0,
      },
    ]);
  };

  const removeEducation = (index) => {
    setEducation((prev) => {
      const newArray = prev.filter((_, i) => i !== index);
      // If the removed row was primary and there is at least one left, set the first one as primary.
      if (prev[index].primary && newArray.length > 0) {
        newArray[0].primary = true;
      }
      return newArray;
    });
  };

  const handleEducationFieldChange = (index, field, value) => {
    setEducation((prev) => {
      const newArray = [...prev];
      newArray[index] = { ...newArray[index], [field]: value };
      return newArray;
    });
  };

  // When a row is marked primary, update all rows so that only this one is primary.
  const handlePrimaryChange = (index) => {
    setEducation((prev) =>
      prev.map((item, i) => ({ ...item, primary: i === index }))
    );
  };

  const handleNext = () => {
    dispatch(
      updateOnboardingInfo({
        education,
        professionalMemberships: selectedMemberships,
      })
    );
  };

  // Disable the Continue button if there are no education entries or if any required field is missing.
  const isFooterDisabled =
    education.length === 0 ||
    education.some(
      (edu) => !edu.institution || !edu.degree || !edu.startYear || !edu.endYear
    );

  return (
    <div className="education-container">
      <div className="category-profile-types">
        <div style={{ display: "flex", flexDirection: "column", width: "40%" }}>
          <div className="category-breadcrumb" onClick={goBackToProfessionals}>
            <img
              src={BackIcon}
              alt="Back Icon"
              style={{ width: "24px", height: "24px" }}
            />
            <a className="category-back-link">Back to professionals</a>
          </div>
          <h1 className="education-title">Education and background</h1>

          <p className="education-second-title">Education history</p>
          <p className="education-subtitle">
            This should include your primary law school and any additional
            education in chronological order.
          </p>

          {education.map((edu, index) => (
            <div key={index} className="education-row">
              <div className="education-row-header">
                <span className="education-number-title">
                  Education {index + 1}
                </span>
                <button
                  className="delete-education-btn"
                  onClick={() => removeEducation(index)}
                  title="Delete education"
                >
                  Delete
                </button>
              </div>
              <NarrowDropdownHierarchy
                title="Institution"
                placeholder="Select an institution"
                items={schools}
                isRequired={true}
                value={edu.institution}
                onChange={(val) =>
                  handleEducationFieldChange(index, "institution", val)
                }
                multiple={false}
                hierarchical={false}
              />
              <NarrowDropdownHierarchy
                title="Degree"
                placeholder="Select a degree"
                items={degrees}
                isRequired={true}
                value={edu.degree}
                onChange={(val) =>
                  handleEducationFieldChange(index, "degree", val)
                }
                multiple={false}
                hierarchical={false}
              />
              <div
                style={{ display: "flex", alignItems: "center", gap: "20px" }}
              >
                <NarrowDropdown2
                  title="Start year"
                  placeholder="Select"
                  items={yearOptions}
                  isRequired={true}
                  value={edu.startYear}
                  onChange={(value) =>
                    handleEducationFieldChange(index, "startYear", value)
                  }
                  multiple={false}
                  halfWidth={true}
                />
                <NarrowDropdown2
                  title="End year"
                  placeholder="Select"
                  items={yearOptions}
                  isRequired={true}
                  value={edu.endYear}
                  onChange={(value) =>
                    handleEducationFieldChange(index, "endYear", value)
                  }
                  multiple={false}
                  halfWidth={true}
                />
              </div>
              <div className="primary-selector">
                <RadioButton
                  label="Primary Law School"
                  name="primaryEducation"
                  value={index}
                  checked={edu.primary}
                  onChange={() => handlePrimaryChange(index)}
                />
              </div>
              <hr className="h-rule" />
            </div>
          ))}

          <button className="education-add-button" onClick={addEducation}>
            <img
              src={AddIcon}
              style={{ width: "16px", height: "16px" }}
              alt="Add"
            />
            Add education
          </button>

          <hr className="h-rule" />

          <p className="education-second-title">
            Board certifications{" "}
            <span className="title-optional-disclaimer">(Optional)</span>
          </p>
          <button className="education-add-button">
            <img
              src={AddIcon}
              style={{ width: "16px", height: "16px" }}
              alt="Add"
            />
            Add certification
          </button>
          <hr className="h-rule" />
          <p className="education-second-title">
            Professional memberships{" "}
            <span className="title-optional-disclaimer">(Optional)</span>
          </p>
          <NarrowDropdownHierarchy
            title="Professional Memberships"
            placeholder="Select your memberships"
            items={professionalMemberships}
            isRequired={false}
            value={selectedMemberships}
            onChange={(vals) => setSelectedMemberships(vals)}
            multiple={true}
            hierarchical={false}
          />
        </div>
      </div>

      <StepFooter
        currentStep={5}
        totalSteps={9}
        location="/attorney/setup/about-you"
        onNext={handleNext}
        disabled={isFooterDisabled} // Disable Continue button if any required education field is missing
      />
    </div>
  );
};

export default Education;
